import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { brandColors } from '@config/brandTokens';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setSuccess(false);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        toast.success(result.message || 'Password reset link has been sent to your email.');
        
        // In development, show the link
        if (result.resetLink && process.env.NODE_ENV === 'development') {
          console.log('Reset link:', result.resetLink);
        }
      } else {
        toast.error(result.message || 'Failed to send reset link');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell>
      <AuthFormCard
        roleLabel="Account"
        title="Forgot password?"
        subtitle="Enter your email and we will send a link to reset your password."
        chrome
        backTo="/login"
      >
          {success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Check your email!
                </Typography>
                <Typography variant="body2">
                  We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
                </Typography>
              </Alert>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/login')}
                sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Back to login
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('email')}
                label="Email Address"
                type="email"
                fullWidth
                margin="normal"
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, ...authPrimaryCtaSx }}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </Button>

              <Box sx={{ mt: 1, textAlign: 'center', ...authLinkSx }}>
                <Link component={RouterLink} to="/login" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <ArrowBackIcon fontSize="small" />
                  <Typography variant="body2" sx={{ color: brandColors.blue }}>
                    Back to login
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default ForgotPasswordPage;
